import React, { useState } from "react";

/**
 * Tiny dependency-free SVG chart primitives used by the Assessments and
 * Attendance Report pages. Not a full charting library — just enough for
 * a bar chart, a stacked bar chart, a line chart, and a donut.
 */

// ─────────────── LineChart ─────────────── //
interface LineChartProps {
  data: { label: string; value: number; extra?: string }[];
  height?: number;
  color?: string;
  tickFormat?: (v: number) => string;
  yMax?: number;
  dotColorFor?: (v: number) => string;
}
export const LineChart: React.FC<LineChartProps> = ({
  data, height = 220, color = "#1b3a6b", tickFormat = (v) => `${v}%`, yMax = 100,
  dotColorFor,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const w = 640, padL = 44, padR = 12, padT = 12, padB = 30;
  const chartW = w - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(1, data.length);
  const stepX = chartW / Math.max(1, n - 1);
  const x = (i: number) => padL + i * stepX;
  const y = (v: number) => padT + chartH - (Math.max(0, Math.min(yMax, v)) / yMax) * chartH;
  const path = data.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d.value)}`).join(" ");
  const areaPath = `${path} L ${x(n - 1)} ${padT + chartH} L ${x(0)} ${padT + chartH} Z`;
  const ticks = [0, 25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ overflow: "visible" }}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={padL + chartW} y1={y((t / 100) * yMax)} y2={y((t / 100) * yMax)}
            stroke="#f1f5f9" strokeWidth={1} />
          <text x={padL - 8} y={y((t / 100) * yMax) + 4}
            fontSize={10} fill="#94a3b8" textAnchor="end">{tickFormat((t / 100) * yMax)}</text>
        </g>
      ))}
      <path d={areaPath} fill={color} opacity={0.08} />
      <path d={path} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => (
        <g key={i}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          style={{ cursor: "default" }}
        >
          <circle
            cx={x(i)} cy={y(d.value)}
            r={hover === i ? 7 : 5}
            fill={dotColorFor ? dotColorFor(d.value) : "#fff"}
            stroke={dotColorFor ? "#fff" : color}
            strokeWidth={dotColorFor ? 2 : 2}
          />
          <text x={x(i)} y={height - 8} fontSize={10} fill="#94a3b8" textAnchor="middle">{d.label}</text>
          {hover === i && (
            <g>
              <rect x={x(i) - (d.extra ? 68 : 34)} y={y(d.value) - (d.extra ? 44 : 30)}
                width={d.extra ? 136 : 68} height={d.extra ? 36 : 22} rx={5} fill="#1e293b" />
              <text x={x(i)} y={y(d.value) - (d.extra ? 28 : 15)} fontSize={11} fill="#fff" textAnchor="middle" fontWeight={700}>
                {tickFormat(d.value)}
              </text>
              {d.extra && (
                <text x={x(i)} y={y(d.value) - 14} fontSize={10} fill="#cbd5e1" textAnchor="middle">
                  {d.extra}
                </text>
              )}
            </g>
          )}
        </g>
      ))}
    </svg>
  );
};

// ─────────────── AreaChart (smooth monotone with gradient fill) ─────────────── //
export interface AreaChartPoint {
  label: string;
  value: number;
  extra?: string;
  /** Arbitrary payload surfaced to renderTooltip. */
  payload?: any;
}
interface AreaChartProps {
  data: AreaChartPoint[];
  height?: number;
  color?: string;
  gradientId?: string;
  tickFormat?: (v: number) => string;
  yMax?: number;
  target?: number;
  targetLabel?: string;
  /** Optional custom tooltip renderer. Receives the hovered point. */
  renderTooltip?: (p: AreaChartPoint) => React.ReactNode;
  /**
   * Dot color rule. Default: green ≥80, amber 50-79, red <50.
   * Pass null to disable per-dot coloring (uses the stroke color).
   */
  dotColorFor?: ((v: number) => string) | null;
  /**
   * Make a single data point read as an intentional design (larger dot +
   * halo + horizontal guide across the plot + inline value label) instead
   * of a stray dot on an empty grid. No effect when data.length !== 1.
   */
  singlePointEmphasis?: boolean;
}
export const AreaChart: React.FC<AreaChartProps> = ({
  data, height = 220, color = "#1b3a6b",
  gradientId = "areaGrad",
  tickFormat = (v) => `${v}%`, yMax = 100,
  target, targetLabel, renderTooltip,
  dotColorFor,
  singlePointEmphasis,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const w = 640, padL = 44, padR = 12, padT = 12, padB = 30;
  const chartW = w - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(1, data.length);
  const stepX = n === 1 ? 0 : chartW / (n - 1);
  const x = (i: number) => padL + (n === 1 ? chartW / 2 : i * stepX);
  const y = (v: number) => padT + chartH - (Math.max(0, Math.min(yMax, v)) / yMax) * chartH;

  // Smooth monotone using cardinal-like control points.
  const pathPts = data.map((d, i) => ({ px: x(i), py: y(d.value) }));
  let linePath = "";
  if (pathPts.length === 1) {
    linePath = `M ${pathPts[0].px} ${pathPts[0].py}`;
  } else {
    linePath = `M ${pathPts[0].px} ${pathPts[0].py}`;
    for (let i = 1; i < pathPts.length; i++) {
      const p0 = pathPts[i - 1];
      const p1 = pathPts[i];
      const cx1 = p0.px + (p1.px - p0.px) / 2;
      const cx2 = cx1;
      linePath += ` C ${cx1} ${p0.py}, ${cx2} ${p1.py}, ${p1.px} ${p1.py}`;
    }
  }
  const areaPath = pathPts.length
    ? `${linePath} L ${pathPts[pathPts.length - 1].px} ${padT + chartH} L ${pathPts[0].px} ${padT + chartH} Z`
    : "";

  const ticks = [0, 25, 50, 75, 100];

  const chart = (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>

      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={padL + chartW} y1={y((t / 100) * yMax)} y2={y((t / 100) * yMax)}
            stroke="#f1f5f9" strokeWidth={1} />
          <text x={padL - 8} y={y((t / 100) * yMax) + 4}
            fontSize={10} fill="#94a3b8" textAnchor="end">{tickFormat((t / 100) * yMax)}</text>
        </g>
      ))}

      {typeof target === "number" && (
        <g>
          <line
            x1={padL} x2={padL + chartW}
            y1={y(target)} y2={y(target)}
            stroke="#ef4444" strokeDasharray="5 4" strokeWidth={1.2}
          />
          {targetLabel && (
            <text x={padL + chartW - 6} y={y(target) - 6} fontSize={10}
              fill="#ef4444" textAnchor="end" fontWeight={700}>
              {targetLabel}
            </text>
          )}
        </g>
      )}

      {pathPts.length > 0 && (
        <>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path d={linePath} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        </>
      )}

      {/* Single-point emphasis: a soft horizontal guide across the plot
          at the point's value, plus a translucent halo behind the dot and
          an inline value label. Makes the panel look intentional instead
          of like a bare grid with a stray point. */}
      {singlePointEmphasis && data.length === 1 && (
        <g>
          <line
            x1={padL} x2={padL + chartW}
            y1={y(data[0].value)} y2={y(data[0].value)}
            stroke={color} strokeOpacity={0.28} strokeWidth={1.5} strokeDasharray="4 4"
          />
          <circle cx={x(0)} cy={y(data[0].value)} r={18}
            fill={color} fillOpacity={0.16} />
          <circle cx={x(0)} cy={y(data[0].value)} r={11}
            fill={color} fillOpacity={0.28} />
          <text
            x={x(0) + 20} y={y(data[0].value) + 4}
            fontSize={13} fontWeight={800} fill={color}
          >{tickFormat(data[0].value)}</text>
        </g>
      )}

      {data.map((d, i) => {
        const defaultDot = (v: number) =>
          v >= 80 ? "#16a34a" : v >= 50 ? "#d97706" : "#ef4444";
        const dotColor = dotColorFor === null
          ? color
          : (dotColorFor ? dotColorFor(d.value) : defaultDot(d.value));
        return (
          <g key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <circle
              cx={x(i)}
              cy={y(d.value)}
              r={hover === i
                ? (singlePointEmphasis && data.length === 1 ? 10 : 8)
                : (singlePointEmphasis && data.length === 1 ? 8 : 6)}
              fill={dotColor} stroke="#fff" strokeWidth={2}
              style={{ cursor: "pointer" }} />
            <text x={x(i)} y={height - 8} fontSize={10} fill="#94a3b8" textAnchor="middle">
              {d.label}
            </text>
            {/* Built-in mini tooltip — only when no custom renderer is supplied */}
            {hover === i && !renderTooltip && (
              <g>
                <rect
                  x={x(i) - (d.extra ? 78 : 40)}
                  y={y(d.value) - (d.extra ? 46 : 32)}
                  width={d.extra ? 156 : 80}
                  height={d.extra ? 38 : 24}
                  rx={6}
                  fill="#1e293b"
                />
                <text x={x(i)} y={y(d.value) - (d.extra ? 30 : 16)}
                  fontSize={11} fill="#fff" textAnchor="middle" fontWeight={700}>
                  {tickFormat(d.value)}
                </text>
                {d.extra && (
                  <text x={x(i)} y={y(d.value) - 14} fontSize={10} fill="#cbd5e1" textAnchor="middle">
                    {d.extra}
                  </text>
                )}
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );

  // Custom-tooltip overlay: wraps the SVG in a positioned div so the
  // renderTooltip content lives in HTML (rich styling, colored badges,
  // multi-line meta) but tracks the hovered dot position.
  if (renderTooltip) {
    const idx = hover;
    const hovered = idx != null ? data[idx] : null;
    // Percent positions inside the viewBox for the overlay tooltip.
    const leftPct = idx != null ? (x(idx) / w) * 100 : 0;
    const topPct = idx != null ? (y(data[idx].value) / height) * 100 : 0;
    return (
      <div style={{ position: "relative" }}>
        {chart}
        {hovered && (
          <div
            style={{
              position: "absolute",
              left: `${leftPct}%`,
              top: `${topPct}%`,
              transform: "translate(-50%, calc(-100% - 14px))",
              pointerEvents: "none",
              zIndex: 10,
            }}
          >
            {renderTooltip(hovered)}
          </div>
        )}
      </div>
    );
  }
  return chart;
};

// ─────────────── BarChart ─────────────── //
interface BarChartProps {
  data: { label: string; value: number; color?: string }[];
  height?: number;
  tickFormat?: (v: number) => string;
  yMax?: number;
  colorFor?: (v: number) => string;
}
export const BarChart: React.FC<BarChartProps> = ({
  data, height = 220, tickFormat = (v) => `${v}%`,
  yMax = 100,
  colorFor = (v) => (v >= 80 ? "#16a34a" : v >= 50 ? "#f59e0b" : "#ef4444"),
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const w = 640, padL = 44, padR = 12, padT = 12, padB = 40;
  const chartW = w - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(1, data.length);
  const bandW = chartW / n;
  const barW = Math.min(36, bandW * 0.65);
  const y = (v: number) => padT + chartH - (Math.max(0, Math.min(yMax, v)) / yMax) * chartH;
  const ticks = [0, 25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ overflow: "visible" }}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={padL + chartW} y1={y((t / 100) * yMax)} y2={y((t / 100) * yMax)}
            stroke="#f1f5f9" strokeWidth={1} />
          <text x={padL - 8} y={y((t / 100) * yMax) + 4}
            fontSize={10} fill="#94a3b8" textAnchor="end">{tickFormat((t / 100) * yMax)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = padL + bandW * i + bandW / 2;
        const bh = padT + chartH - y(d.value);
        const color = d.color || colorFor(d.value);
        return (
          <g key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <rect
              x={cx - barW / 2} y={y(d.value)}
              width={barW} height={bh}
              rx={6}
              fill={color}
              opacity={hover === i ? 0.85 : 1}
            />
            <text x={cx} y={height - 20} fontSize={10} fill="#94a3b8"
              textAnchor="end" transform={`rotate(-30, ${cx}, ${height - 20})`}>
              {d.label.length > 14 ? d.label.slice(0, 14) + "…" : d.label}
            </text>
            {hover === i && (
              <g>
                <rect x={cx - 30} y={y(d.value) - 24} width={60} height={20} rx={5} fill="#1e293b" />
                <text x={cx} y={y(d.value) - 10} fontSize={11} fill="#fff" textAnchor="middle" fontWeight={700}>
                  {tickFormat(d.value)}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─────────────── Stacked BarChart (Attendance) ─────────────── //
interface StackedRow { label: string; present: number; absent: number; late: number; }
export const StackedBarChart: React.FC<{ data: StackedRow[]; height?: number }> = ({
  data, height = 220,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const w = 640, padL = 44, padR = 12, padT = 12, padB = 40;
  const chartW = w - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(1, data.length);
  const bandW = chartW / n;
  const barW = Math.min(28, bandW * 0.6);
  const maxY = Math.max(1, ...data.map((d) => d.present + d.absent + d.late));
  const yMax = Math.ceil(maxY / 5) * 5 || 5;
  const y = (v: number) => padT + chartH - (v / yMax) * chartH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * yMax));

  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ overflow: "visible" }}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={padL + chartW} y1={y(t)} y2={y(t)} stroke="#f1f5f9" strokeWidth={1} />
          <text x={padL - 8} y={y(t) + 4} fontSize={10} fill="#94a3b8" textAnchor="end">{t}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = padL + bandW * i + bandW / 2;
        const total = d.present + d.absent + d.late;
        const yTop = y(total);
        const pH = (d.present / (total || 1)) * (padT + chartH - yTop);
        const aH = (d.absent / (total || 1)) * (padT + chartH - yTop);
        const lH = (d.late / (total || 1)) * (padT + chartH - yTop);
        return (
          <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <rect x={cx - barW / 2} y={yTop} width={barW} height={pH} fill="#16a34a" rx={0} />
            <rect x={cx - barW / 2} y={yTop + pH} width={barW} height={aH} fill="#ef4444" />
            <rect x={cx - barW / 2} y={yTop + pH + aH} width={barW} height={lH} fill="#f59e0b" />
            <text x={cx} y={height - 20} fontSize={10} fill="#94a3b8"
              textAnchor="end" transform={`rotate(-30, ${cx}, ${height - 20})`}>
              {d.label.length > 12 ? d.label.slice(0, 12) + "…" : d.label}
            </text>
            {hover === i && (
              <g>
                <rect x={cx - 42} y={yTop - 40} width={84} height={34} rx={5} fill="#1e293b" />
                <text x={cx} y={yTop - 24} fontSize={10} fill="#86efac" textAnchor="middle">P {d.present}</text>
                <text x={cx} y={yTop - 12} fontSize={10} fill="#fca5a5" textAnchor="middle">A {d.absent} · L {d.late}</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─────────────── HorizontalBarChart ─────────────── //
interface HBarProps {
  data: { label: string; value: number; color?: string }[];
  height?: number;
  tickFormat?: (v: number) => string;
  yMax?: number;
  barSize?: number;
  colorFor?: (v: number) => string;
}
export const HorizontalBarChart: React.FC<HBarProps> = ({
  data, tickFormat = (v) => `${v}%`, yMax = 100, barSize = 18,
  colorFor = (v) => (v >= 80 ? "#16a34a" : v >= 50 ? "#f59e0b" : "#ef4444"),
  height,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const rowH = barSize + 26;
  const padL = 88, padR = 60, padT = 8, padB = 8;
  const chartH = data.length * rowH;
  const totalH = height || padT + padB + chartH;
  const w = 640;
  const chartW = w - padL - padR;
  const x = (v: number) => padL + (Math.max(0, Math.min(yMax, v)) / yMax) * chartW;

  return (
    <svg viewBox={`0 0 ${w} ${totalH}`} width="100%" height={totalH} style={{ overflow: "visible" }}>
      {data.map((d, i) => {
        const y = padT + i * rowH;
        const color = d.color || colorFor(d.value);
        return (
          <g key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <text x={padL - 10} y={y + barSize / 2 + 4} fontSize={12}
              fill="#334155" textAnchor="end" fontWeight={600}>
              {d.label.length > 14 ? d.label.slice(0, 14) + "…" : d.label}
            </text>
            <rect x={padL} y={y} width={chartW} height={barSize} rx={6}
              fill="#f1f5f9" />
            <rect x={padL} y={y} width={x(d.value) - padL} height={barSize} rx={6}
              fill={color} opacity={hover === i ? 0.85 : 1} />
            <text x={x(d.value) + 8} y={y + barSize / 2 + 4} fontSize={12}
              fill={color} textAnchor="start" fontWeight={800}>
              {tickFormat(d.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─────────────── GroupedBarChart (Present/Absent/Late) ─────────────── //
interface GroupedRow { label: string; present: number; absent: number; late: number; }
export const GroupedBarChart: React.FC<{
  data: GroupedRow[];
  height?: number;
}> = ({ data, height = 200 }) => {
  const [hover, setHover] = useState<number | null>(null);
  const w = 640, padL = 36, padR = 12, padT = 12, padB = 40;
  const chartW = w - padL - padR;
  const chartH = height - padT - padB;
  const n = Math.max(1, data.length);
  const bandW = chartW / n;
  const groupInner = bandW * 0.75;
  const barW = Math.max(6, Math.min(14, groupInner / 3 - 2));
  const maxY = Math.max(1, ...data.map((d) => Math.max(d.present, d.absent, d.late)));
  const yMax = Math.ceil(maxY / 5) * 5 || 5;
  const y = (v: number) => padT + chartH - (v / yMax) * chartH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(f * yMax));
  const COLORS = { present: "#16a34a", absent: "#ef4444", late: "#f59e0b" };

  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} style={{ overflow: "visible" }}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} x2={padL + chartW} y1={y(t)} y2={y(t)} stroke="#f1f5f9" strokeWidth={1} />
          <text x={padL - 6} y={y(t) + 4} fontSize={10} fill="#94a3b8" textAnchor="end">{t}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = padL + bandW * i + bandW / 2;
        const startX = cx - (barW * 3 + 4) / 2;
        const bars: [keyof typeof COLORS, number][] = [
          ["present", d.present],
          ["absent", d.absent],
          ["late", d.late],
        ];
        return (
          <g key={i}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            {bars.map(([k, v], j) => (
              <rect
                key={k}
                x={startX + j * (barW + 2)}
                y={y(v)}
                width={barW}
                height={padT + chartH - y(v)}
                fill={COLORS[k]}
                opacity={hover === i ? 0.85 : 1}
                rx={3}
              />
            ))}
            <text x={cx} y={height - 20} fontSize={10} fill="#94a3b8"
              textAnchor="end" transform={`rotate(-30, ${cx}, ${height - 20})`}>
              {d.label.length > 10 ? d.label.slice(0, 10) + "…" : d.label}
            </text>
            {hover === i && (
              <g>
                <rect x={cx - 42} y={y(Math.max(d.present, d.absent, d.late)) - 44}
                  width={84} height={38} rx={5} fill="#1e293b" />
                <text x={cx} y={y(Math.max(d.present, d.absent, d.late)) - 30}
                  fontSize={10} fill="#86efac" textAnchor="middle">P {d.present}</text>
                <text x={cx} y={y(Math.max(d.present, d.absent, d.late)) - 18}
                  fontSize={10} fill="#fca5a5" textAnchor="middle">A {d.absent}</text>
                <text x={cx} y={y(Math.max(d.present, d.absent, d.late)) - 6}
                  fontSize={10} fill="#fbbf24" textAnchor="middle">L {d.late}</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─────────────── Donut ─────────────── //
export interface DonutSlice {
  label: string;
  value: number;
  color: string;
  /** Arbitrary payload passed to renderTooltip. */
  payload?: any;
}
interface DonutProps {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerSub?: string;
  /** Optional rich tooltip renderer (rendered as an HTML overlay). */
  renderTooltip?: (s: DonutSlice) => React.ReactNode;
}
export const DonutChart: React.FC<DonutProps> = ({
  slices, size = 180, thickness = 26, centerLabel, centerSub, renderTooltip,
}) => {
  const total = slices.reduce((s, x) => s + x.value, 0);
  const r = size / 2 - thickness / 2;
  const cx = size / 2, cy = size / 2;
  const [hover, setHover] = useState<number | null>(null);

  const arc = (start: number, end: number) => {
    const large = end - start > Math.PI ? 1 : 0;
    const sx = cx + r * Math.cos(start), sy = cy + r * Math.sin(start);
    const ex = cx + r * Math.cos(end), ey = cy + r * Math.sin(end);
    return `M ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey}`;
  };

  // Precompute slice angles so hover can position the tooltip at the slice
  // midpoint.
  const angles: { start: number; end: number; mid: number }[] = [];
  {
    let acc = 0;
    slices.forEach((s) => {
      const frac = total > 0 ? s.value / total : 0;
      const start = acc * Math.PI * 2 - Math.PI / 2;
      acc += frac;
      const end = acc * Math.PI * 2 - Math.PI / 2;
      angles.push({ start, end, mid: (start + end) / 2 });
    });
  }

  const chart = (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {total === 0 ? (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={thickness} />
      ) : (
        slices.map((s, i) => {
          if (s.value === 0) return null;
          const a = angles[i];
          const active = hover === i;
          const isFullCircle = Math.abs((a.end - a.start) - Math.PI * 2) < 1e-6;
          // SVG arc from a point to itself renders nothing — for a slice
          // that spans the entire ring (the only category present), draw
          // a plain circle instead so the donut has a visible ring.
          if (isFullCircle) {
            return (
              <circle
                key={i}
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={active ? thickness + 4 : thickness}
                style={{ cursor: "pointer", transition: "stroke-width 0.15s" }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            );
          }
          return (
            <path
              key={i}
              d={arc(a.start, a.end)}
              stroke={s.color}
              strokeWidth={active ? thickness + 4 : thickness}
              fill="none"
              strokeLinecap="butt"
              style={{ cursor: "pointer", transition: "stroke-width 0.15s" }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          );
        })
      )}
      {centerLabel && (
        <text x={cx} y={cy - 4} fontSize={22} fontWeight={800}
          textAnchor="middle" fill="#1b3a6b">{centerLabel}</text>
      )}
      {centerSub && (
        <text x={cx} y={cy + 16} fontSize={11} textAnchor="middle" fill="#94a3b8">{centerSub}</text>
      )}
    </svg>
  );

  if (renderTooltip && hover != null && slices[hover]) {
    const a = angles[hover];
    // Compute tooltip anchor position (percent of size) at slice midpoint.
    const tx = cx + r * Math.cos(a.mid);
    const ty = cy + r * Math.sin(a.mid);
    return (
      <div style={{ position: "relative", display: "inline-block" }}>
        {chart}
        <div
          style={{
            position: "absolute",
            left: `${(tx / size) * 100}%`,
            top: `${(ty / size) * 100}%`,
            transform: "translate(-50%, -110%)",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {renderTooltip(slices[hover])}
        </div>
      </div>
    );
  }
  return chart;
};
